import { KeyDate } from 'routes/Home/models/KeyDate';

/**
 * This helper is used to compute the stepper active step. The rule here is simple :
 * We keep only the keyDates that have a scheduled Date before or equal to today.
 * We return -1 if there is not passed keyDates. If there are any, we pick the last based on the original events.
 *
 * @param keyDates - The Budget KeyDates fetched from the backend.
 * @param budgetKeyDatesEvent - The list of Budget KeyDates event names ('entry_opening', 'entry_closing', ...).
 */
export const getActiveStep = (
    keyDates: KeyDate[],
    budgetKeyDatesEvent: string[],
): number => {
    const passedDates = keyDates.filter(
        (date) => date.scheduledAt && new Date(date.scheduledAt) <= new Date(),
    ) as Required<KeyDate>[];

    if (passedDates.length === 0) {
        return -1;
    }

    const lastPassedKeyDate = passedDates[passedDates.length - 1];
    return budgetKeyDatesEvent.findIndex(
        (event) => event === lastPassedKeyDate.event,
    );
};

/**
 * This helper returns the label of the keyDate. If the key date event is one
 * of the main budget key dates event. We return a set translation label. Else,
 * we return the event name.
 *
 * @param keyDate - The keyDate object.
 * @param t - i18n Translation function
 */
export const getKeyDateLabel = (keyDate: KeyDate, t: TranslationT): string => {
    const DEFAULT_KEY_DATES_MAP: Record<string, string> = {
        entry_opening: t('home.keyDates.entry_opening'),
        entry_closing: t('home.keyDates.entry_closing'),
        budget_vote: t('home.keyDates.budget_vote'),
        supplementary_budget_opening: t(
            'home.keyDates.supplementary_budget_opening',
        ),
    };

    return DEFAULT_KEY_DATES_MAP[keyDate.event] ?? keyDate.event;
};

export const getFormattedScheduledDate = (
    scheduledAt: string | undefined,
    t: TranslationT,
): string => {
    if (!scheduledAt) {
        return t('home.keyDates.empty');
    }
    const date = new Date(scheduledAt);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
};
